import { Key } from "client/Misc/commonTypes";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { ResolvedPos, Schema } from "prosemirror-model";
import { Plugin, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { LinkMarkAttributes } from "../schema/marks/link";

export function linkifyPlugin(schema: Schema<never, "link">): Plugin<{}> {
	return new Plugin<{}>({
		props: {
			handleKeyDown(view: EditorView, event: KeyboardEvent): boolean {
				const key: Key | null = tryResolveKeyboardKey(event);
				if (key !== Key.Enter) {
					return false;
				}

				const {
					$from,
					empty
				} = view.state.selection;

				if (!empty) {
					return false;
				}

				return run(
					view,
					$from,
					schema
				);
			},
			handleTextInput(
				view: EditorView,
				from: number,
				_to: number,
				text: string
			): boolean {
				if (text !== " ") {
					return false;
				}

				const $from: ResolvedPos = view.state
					.doc
					.resolve(from);

				return run(
					view,
					$from,
					schema
				);
			},
		}
	});
}

const MAX_MATCH = 500;
const urlRegex = /(?:\s|^)(https?:\/\/[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*))$/;

function run(
	view: EditorView,
	$from: ResolvedPos,
	schema: Schema<never, "link">
): boolean {
	if (view.composing) {
		return false;
	}

	const {
		state
	} = view;

	const {
		parent,
		parentOffset,
		pos
	} = $from;

	if (parent.type
		.spec
		.code
	) {
		return false;
	}

	const textBefore: string = parent.textBetween(
		Math.max(
			0,
			parentOffset - MAX_MATCH
		),
		parentOffset,
		undefined,
		"\ufffc"
	);

	const match: RegExpExecArray | null = urlRegex.exec(textBefore);

	if (!match) {
		return false;
	}

	const url: string = match[1];

	const linkAttributes: LinkMarkAttributes = {
		href: url
	};

	const from: number = pos - url.length;
	const to: number = pos;

	if (state.doc
		.rangeHasMark(from, to, schema.marks.link)
	) {
		return false;
	}

	const tr: Transaction = state.tr;

	tr.addMark(
		from,
		to,
		schema.marks
			.link
			.create(
				linkAttributes
			)
	);

	view.dispatch(tr);

	return false;
}